import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LegalShell from '../components/LegalShell';
import { Button } from '../components/ui/button';
import { getConsent, setConsent, subscribeConsent } from '../analytics/consent';
import { updateConsent } from '../analytics/ga';

// The mandated easy-withdrawal path: shows the current analytics-consent
// status and lets the reader change it right where the cookies are explained.
const CookieSettings = () => {
  const [choice, setChoice] = useState(() => getConsent());
  useEffect(() => subscribeConsent(setChoice), []);

  const decide = (granted) => {
    setConsent(granted);
    updateConsent(granted);
  };

  const status = choice === null
    ? 'you have not made a choice yet.'
    : choice.analytics
      ? 'analytics cookies are allowed.'
      : 'analytics cookies are declined.';

  return (
    <div className="legal-consent">
      <p className="legal-p">
        <strong>Cookie settings</strong> — your current setting: {status}
      </p>
      <div className="legal-consent__actions">
        <Button variant="outline" size="sm" onClick={() => decide(false)}>Decline analytics</Button>
        <Button variant="outline" size="sm" onClick={() => decide(true)}>Allow analytics</Button>
      </div>
    </div>
  );
};

const Privacy = () => (
  <LegalShell title="Privacy" accent="policy">
    <section className="legal-section">
      <h2 className="legal-h2">1. Controller and contact details</h2>
      <p className="legal-p">
        The controller within the meaning of Art. 4(7) GDPR is:
      </p>
      {/* Art. 3:15d BW: add the KvK number and VAT identification number here
          once Qombra B.V. is registered. */}
      <p className="legal-addr">
        <strong>Qombra B.V.</strong><br />
        Valckenierstraat 65<br />
        1018 WV Amsterdam<br />
        The Netherlands<br />
        Email: <a className="legal-link" href="mailto:hey@qombra.com">hey[at]qombra.com</a>
      </p>
      <p className="legal-p">
        ("we", "us" or "Qombra"). We have not appointed a data protection officer: our own
        processing involves neither regular and systematic monitoring of data subjects on a
        large scale nor large-scale processing of special categories of data (Art. 37(1) GDPR).
        We review this as our services develop.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">2. Scope and our two roles</h2>
      <p className="legal-p">
        This policy explains how we process personal data when you visit our website, create an
        account, use the Qombra platform, the QBrain API and SDK or QAgent, or contact us.
      </p>
      <p className="legal-p">
        <strong>As controller</strong> we process the data of website visitors, account holders,
        users acting for a customer organisation, and contacts (sections 3 to 8).
      </p>
      <p className="legal-p">
        <strong>As processor</strong> we process the content our customers upload or make
        available through the platform, the API, the SDK or QAgent, on their instructions. Where
        that content contains personal data, the customer is the controller. Section 9 explains
        what this means if your data is in a customer's table.
      </p>
      <p className="legal-p">
        We do not use advertising technologies. On our public website we use Google Analytics
        only with your prior consent (sections 10 and 11). Within the logged-in platform we use
        only strictly necessary cookies and no analytics.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">3. Website visit</h2>
      <p className="legal-p">
        When you access our website, your browser transmits the IP address of your device, the
        date and time of access, the page requested, the referring page, and browser and
        operating system information. We process these data to deliver the website and to keep
        it stable and secure. We do not build profiles from them.
      </p>
      <p className="legal-p">
        <strong>Legal basis:</strong> Art. 6(1)(f) GDPR; our legitimate interest is the secure
        and error-free provision of the website.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">4. Account and platform use</h2>
      <p className="legal-p">
        When you register we process your first and last name, email address, password (stored
        only as a hash), company name (optional), the use case you select, the date and version
        of the Terms you accepted, and account metadata (registration date, last login). While
        you use the platform we process your actions in the platform, the analyses you create and
        usage counters for the applicable limits.
      </p>
      <p className="legal-p">
        <strong>Purposes:</strong> creating and securing your account, authenticating you,
        providing the platform, enforcing usage limits, and showing you a demo analysis for the
        use case you selected.
      </p>
      <p className="legal-p">
        <strong>Legal basis:</strong> Art. 6(1)(b) GDPR where you are the contracting party.
        Where you use an account on behalf of your employer or another organisation that is our
        customer, Art. 6(1)(f) GDPR; our legitimate interest, and that of the customer, is
        providing the contracted service to the organisation through its users.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">5. QBrain API and SDK</h2>
      <p className="legal-p">
        When you use the API or the SDK we process the API keys and session tokens you issue
        (stored only as hashes, with a short prefix for identification), the time each key was
        last used, and request metadata: IP address, timestamp, endpoint, request size, response
        status and processing time. We do not log the contents of your requests, tables or
        predictions. Datasets, models and results you create through the API are stored in your
        account like any other platform content.
      </p>
      <p className="legal-p">
        <strong>Purposes and legal bases:</strong> delivering the API (Art. 6(1)(b) GDPR, or
        Art. 6(1)(f) for users acting for a customer organisation); security, abuse prevention,
        rate limiting and usage metering (Art. 6(1)(f) GDPR; our legitimate interest is
        protecting the service and charging correctly).
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">6. QAgent</h2>
      <p className="legal-p">
        QAgent is an AI system. When you use it we process your messages, the chat history of the
        analysis, the data it works on, the code it generates and the results of running that
        code. These are stored with the analysis so you can return to the conversation.
      </p>
      <p className="legal-p">
        QAgent's answers are generated by Anthropic Claude language models that we run through
        Amazon Bedrock in the EU region of Amazon Web Services (section 8). Prompts and outputs
        are processed in the EU, are not stored by AWS or the model vendor after the request, and
        are not used to train models. Our own foundation models for predictions (QBrain) are not
        language models and receive only the table you analyse.
      </p>
      <p className="legal-p">
        <strong>Legal basis:</strong> Art. 6(1)(b) GDPR, or Art. 6(1)(f) for users acting for a
        customer organisation. Personal data inside the tables QAgent works on is processed on
        behalf of the customer (section 9).
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">7. Contact, support, billing and other purposes</h2>
      <p className="legal-p">
        <strong>Contact and support.</strong> When you email us, book a demo or ask for API
        access, we process your name, email address, company and the content of your message to
        handle your request. Legal basis: Art. 6(1)(b) GDPR for steps before or under a
        contract, otherwise Art. 6(1)(f) GDPR (responding to enquiries).
      </p>
      <p className="legal-p">
        <strong>Billing.</strong> For paid use we process the contact and invoicing details of
        the customer and its contact persons. Legal basis: Art. 6(1)(b) GDPR and, for keeping
        invoices and tax records, Art. 6(1)(c) GDPR in connection with Dutch tax law. We do not
        use a payment provider; invoices are paid by bank transfer.
      </p>
      <p className="legal-p">
        <strong>Transactional emails.</strong> We send account emails (confirmation, password
        reset, notifications about your analyses). Legal basis: Art. 6(1)(b) GDPR.
      </p>
      <p className="legal-p">
        <strong>Product updates.</strong> We may inform existing customers by email about changes
        to the services and about similar services of ours. You can opt out in every message or
        by emailing us. Legal basis: Art. 6(1)(f) GDPR and Art. 11.7 of the Dutch
        Telecommunications Act.
      </p>
      <p className="legal-p">
        <strong>Security and abuse prevention</strong> across the website, platform and API, and{' '}
        <strong>establishing, exercising or defending legal claims</strong>. Legal basis:
        Art. 6(1)(f) GDPR.
      </p>
      <p className="legal-p">
        We do not make decisions about you based solely on automated processing within the
        meaning of Art. 22 GDPR. Where a customer uses predictions from our services to make
        decisions about individuals, that use is the customer's decision as controller.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">8. Processors, infrastructure and transfers</h2>
      <p className="legal-p">
        The platform and all customer data are hosted in <strong>Germany (AWS Frankfurt
        region)</strong> by Amazon Web Services EMEA SARL, 38 avenue John F. Kennedy, L-1855
        Luxembourg, under a data processing agreement. AWS also provides the email service
        (Amazon SES) and the language-model service for QAgent (Amazon Bedrock, EU region). No
        transfer outside the European Economic Area takes place in connection with hosting, email
        or language models.
      </p>
      <p className="legal-p">
        Model training and prediction jobs run on GPU servers of Runpod, Inc., Mount Laurel, New
        Jersey, United States, under a data processing agreement. The servers that process
        dataset contents are located exclusively in the European Union; we restrict processing to
        EU data centres technically. Dataset copies are transferred over TLS, encrypted at rest,
        used only for the duration of the job and deleted afterwards, at the latest within 24
        hours. Only job coordination metadata (job identifiers, status and timing, without
        dataset contents or column names) reaches Runpod's US systems; this transfer is
        safeguarded by Standard Contractual Clauses (Art. 46(2)(c) GDPR).
      </p>
      <p className="legal-p">
        Google Ireland Limited processes website analytics data only with your consent (section
        10). The current list of processors for customer content is published as Annex 3 of our{' '}
        <Link className="legal-link" to="/dpa#sub-processors">Data Processing Agreement</Link>.
      </p>
      <p className="legal-p">
        <strong>Models and training.</strong> QBrain's foundation models are pretrained on
        synthetically generated tables; no real datasets, and no customer data, are used for
        that. Models trained on your data belong to your account, are used only for you, and are
        deleted when you delete them or your account.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">9. If your data is in a customer's table</h2>
      <p className="legal-p">
        Our customers upload tables and connect data sources that may contain personal data about
        their customers, users or employees. For that data the customer is the controller and we
        are its processor under a <Link className="legal-link" to="/dpa">Data Processing
        Agreement</Link>. We process it only on the customer's instructions, do not use it for
        our own purposes and never use it to train models for other customers.
      </p>
      <p className="legal-p">
        To exercise your rights regarding such data, please contact the organisation that uses
        our services. If you contact us, we forward your request to that customer and refer you
        to it. Whether sensitive data may be uploaded, and on what terms, is governed by the Data
        Processing Agreement and the Acceptable Use Policy.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">10. Web analytics (Google Analytics)</h2>
      <p className="legal-p">
        On our <strong>public website</strong> (not within the logged-in platform) we use Google
        Analytics 4, provided by Google Ireland Limited, Gordon House, Barrow Street, Dublin 4,
        Ireland (parent company: Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043,
        USA).
      </p>
      <p className="legal-p">
        <strong>What is collected:</strong> a pseudonymous client identifier stored in a cookie,
        a truncated IP address, device and browser information, the pages you visit, buttons and
        links you click, the referring page, and your approximate (city-level) location. We do
        not use Google Analytics for advertising, do not enable Google Signals, and do not pass
        account data to Google.
      </p>
      <p className="legal-p">
        <strong>Consent:</strong> the Google Analytics script is loaded, and data is collected,
        only after you accept analytics in the cookie banner. Before that, nothing is sent to
        Google and no identifier is stored on your device.
      </p>
      <p className="legal-p">
        <strong>Legal basis:</strong> your consent, Art. 6(1)(a) GDPR and Art. 11.7a of the Dutch
        Telecommunications Act. You can withdraw it at any time with effect for the future in
        section 11.
      </p>
      <p className="legal-p">
        <strong>Transfer to the USA:</strong> data may be transferred to Google LLC. Google LLC is
        certified under the EU-US Data Privacy Framework (adequacy decision, Art. 45 GDPR);
        Google additionally offers Standard Contractual Clauses (Art. 46(2)(c) GDPR).
      </p>
      <p className="legal-p">
        <strong>Retention:</strong> user- and event-level data in Google Analytics is deleted
        after at most 2 months.
      </p>
    </section>

    <section className="legal-section" id="cookies">
      <h2 className="legal-h2">11. Cookies</h2>
      <p className="legal-p">
        <strong>Strictly necessary cookies</strong> keep you logged in after sign-in and protect
        forms against cross-site request forgery. They are deleted when your session ends or you
        log out and do not require consent. Your cookie choice itself is stored in your browser's
        local storage for 12 months.
      </p>
      <p className="legal-p">
        <strong>Analytics cookies (only with your consent).</strong> If you accept analytics,
        Google Analytics sets the following cookies on our public website:
      </p>
      <ul className="legal-ul">
        <li><strong>_ga</strong> — distinguishes visitors (pseudonymous ID); lifetime 2 years</li>
        <li><strong>_ga_*</strong> — maintains the session state for our property; lifetime 2 years</li>
      </ul>
      <p className="legal-p">
        We do not use advertising cookies. You can change your choice at any time here:
      </p>
      <CookieSettings />
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">12. Retention periods</h2>
      <p className="legal-p">
        We keep personal data only as long as needed for the purposes above or as required by
        law. Deleted data may persist in encrypted backups for up to 35 days.
      </p>
      <table className="legal-table">
        <thead>
          <tr><th>Data</th><th>Retention</th></tr>
        </thead>
        <tbody>
          <tr><td>Account data</td><td>Until you delete your account; then deleted without undue delay, except data we must keep by law.</td></tr>
          <tr><td>Datasets, models, predictions, chat history, API results</td><td>Until you delete them or your account. Copies on GPU servers: at most 24 hours after the job.</td></tr>
          <tr><td>API keys</td><td>Until you revoke them or delete your account.</td></tr>
          <tr><td>Server and API request logs</td><td>At most 30 days, unless needed longer for a specific security incident.</td></tr>
          <tr><td>Support and contact correspondence</td><td>Until the request is resolved and, where needed for legal claims, up to 5 years thereafter (general limitation period, Art. 3:307 of the Dutch Civil Code).</td></tr>
          <tr><td>Invoices and tax records</td><td>7 years (Dutch fiscal retention obligation).</td></tr>
          <tr><td>Web analytics data</td><td>At most 2 months in Google Analytics; cookies as stated in section 11.</td></tr>
        </tbody>
      </table>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">13. Provision of data</h2>
      <p className="legal-p">
        You are not obliged to provide personal data to browse our website; technical data such
        as your IP address is needed to establish the connection. To create an account you must
        provide your name and email address, confirm business use and accept the Terms of
        Service; without these we cannot provide the service. Company name is optional.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">14. Your rights</h2>
      <p className="legal-p">
        Under the GDPR you have, subject to its conditions, the right of access (Art. 15),
        rectification (Art. 16), erasure (Art. 17; you can also delete your account in the
        platform), restriction of processing (Art. 18), data portability (Art. 20; you can export
        your datasets and results from the platform and the SDK), and the right to withdraw
        consent at any time with effect for the future (Art. 7(3); for analytics via section 11).
      </p>
      <div className="legal-callout">
        <p className="legal-p">
          <strong>Right to object (Art. 21 GDPR).</strong> Where we process your personal data on
          the basis of legitimate interests (sections 3 to 7), you may object at any time on
          grounds relating to your particular situation. We then stop the processing unless we
          demonstrate compelling legitimate grounds that override your interests, rights and
          freedoms, or the processing serves legal claims. You may object to product-update
          emails at any time without giving reasons.
        </p>
      </div>
      <p className="legal-p">
        You also have the right to lodge a complaint with a supervisory authority, in particular
        in the EU/EEA Member State of your residence, workplace or the alleged infringement. In
        the Netherlands this is the Autoriteit Persoonsgegevens,{' '}
        <a
          className="legal-link"
          href="https://autoriteitpersoonsgegevens.nl/"
          target="_blank"
          rel="noopener noreferrer"
        >
          autoriteitpersoonsgegevens.nl
        </a>.
      </p>
      <p className="legal-p">
        To exercise your rights, contact us using the details in section 1.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">15. Security</h2>
      <ul className="legal-ul">
        <li>Platform and account data are stored in Germany and do not leave the EEA in normal operations (website analytics excepted, section 10).</li>
        <li>All data is encrypted at rest and in transit (TLS/HTTPS).</li>
        <li>Passwords, API keys and session tokens are stored only as hashes.</li>
        <li>Access to personal data is restricted to personnel who need it to operate and support the service.</li>
        <li>Rate limiting, abuse detection and short-lived staging storage for GPU jobs.</li>
      </ul>
      <p className="legal-p">
        We review these measures in line with technological developments and legal requirements.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">16. Special categories of data and minors</h2>
      <p className="legal-p">
        In our own processing we do not intentionally process special categories of personal data
        (Art. 9 GDPR). Whether customers may upload such data in their tables is governed by the
        Data Processing Agreement and the Acceptable Use Policy.
      </p>
      <p className="legal-p">
        Our services are offered to businesses and are not directed at persons under 18. If we
        learn that we hold account data of a minor, we delete it without undue delay.
      </p>
    </section>

    <section className="legal-section">
      <h2 className="legal-h2">17. Changes to this policy</h2>
      <p className="legal-p">
        We update this policy when our services, our providers or the law change. The current
        version is always available on this page and identified by the status date above. We
        inform you of material changes by email or by a notice in the platform.
      </p>
    </section>
  </LegalShell>
);

export default Privacy;
